import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import dayjs from "dayjs";

import Breadcrumbs from "../../../../components/BreadCrumbs";
import { UIPaginationBar } from "../../../../components/ui";
import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { isSuperAdmin } from "../../../../utils/Permissions";
import {
  getCompaniesListRequest,
  setCompaniesListSearch,
  setCompaniesListFilters,
  setCompaniesListPage,
  setCompaniesListPageSize,
} from "../../../../store/CompaniesList/actions";

import CompaniesTable from "./components/CompaniesTable";
import CompaniesEmptyState from "./components/CompaniesEmptyState/CompaniesEmptyState";
import CompaniesListFilter from "./CompaniesListFilter";

import "./CompaniesList.scss";

const CompaniesList = () => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const isLoading = useSelector((state) => state.loader);
  const { list, loading, pagination, filters } = useSelector(
    (state) => state.companiesList
  );
  const { shared, companiesList: cl } = messages[lang];

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [clearFilters, setClearFilters] = useState(false);
  const lastDispatchedSearch = useRef(null);

  const statusOptions = [
    { id: 1, name: cl.active, value: "approved" },
    { id: 2, name: cl.deactivated, value: "deactivated" },
  ];

  const statusFilterValue = statusOptions.filter((o) =>
    filters.status?.includes(o.value)
  );

  const endDateBeforeStart =
    !!filters.created_at_start_date &&
    !!filters.created_at_end_date &&
    dayjs(filters.created_at_end_date).isBefore(
      dayjs(filters.created_at_start_date),
      "day"
    );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const next = debouncedSearch?.trim() ?? "";
    if (lastDispatchedSearch.current === next) return;
    lastDispatchedSearch.current = next;
    dispatch(setCompaniesListSearch(next));
  }, [dispatch, debouncedSearch]);

  useEffect(() => {
    if (clearFilters) setClearFilters(false);
  }, [clearFilters]);

  useEffect(() => {
    dispatch(getCompaniesListRequest());
  }, [
    dispatch,
    lang,
    pagination.page,
    pagination.per_page,
    filters.search,
    filters.status,
    filters.created_at_start_date,
    filters.created_at_end_date,
  ]);

  if (!isSuperAdmin()) {
    return <Redirect to={ROUTE_PATHS.events} />;
  }

  const from =
    pagination.total === 0
      ? 0
      : (pagination.currentPage - 1) * pagination.per_page + 1;
  const to = Math.min(
    pagination.currentPage * pagination.per_page,
    pagination.total
  );
  const rangeText = cl.showingRange
    .replace("{from}", String(from))
    .replace("{to}", String(to))
    .replace("{total}", String(pagination.total));

  const renderBody = () => {
    if (isLoading || loading) return null;
    const rows = list || [];
    const isEmpty = rows.length === 0;
    return (
      <>
        <CompaniesTable
          rows={rows}
          lang={lang}
          labels={cl}
          shared={shared}
          emptyState={
            isEmpty ? (
              <CompaniesEmptyState
                title={cl.noCompaniesTitle}
                description={cl.noCompaniesDescription}
              />
            ) : null
          }
        />
        {!isEmpty && (
          <UIPaginationBar
            currentPage={pagination.currentPage || pagination.page}
            pages={pagination.pages || 1}
            pageSize={pagination.per_page}
            rangeText={rangeText}
            rowsPerPageLabel={shared.numberOfRows}
            onPageChange={(next) => dispatch(setCompaniesListPage(next))}
            onPageSizeChange={(nextSize) =>
              dispatch(setCompaniesListPageSize(nextSize))
            }
          />
        )}
      </>
    );
  };

  return (
    <div className="companies-list-view">
        <div className="mt-2">
          <Breadcrumbs
            list={[{ path: "", label: cl.listBreadcrumb }]}
            BreadCrumbHasBackIcon={false}
          />
        </div>

        <CompaniesListFilter
          labels={cl}
          shared={shared}
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          statusFilterValue={statusFilterValue}
          statusOptions={statusOptions}
          onStatusChange={(opts) =>
            dispatch(setCompaniesListFilters({ status: opts.map((o) => o.value) }))
          }
          createdFrom={filters.created_at_start_date}
          createdTo={filters.created_at_end_date}
          onCreatedFromChange={(value) =>
            dispatch(setCompaniesListFilters({ created_at_start_date: value }))
          }
          onCreatedToChange={(value) =>
            dispatch(setCompaniesListFilters({ created_at_end_date: value }))
          }
          endDateBeforeStart={endDateBeforeStart}
          clearFilters={clearFilters}
        />

        <div className="companies-list-view__table-shell">
          {renderBody()}
        </div>
    </div>
  );
};

export default CompaniesList;
